import { useState } from "react";
import axios from "axios";

export default function useCancelToken(autoCancel = false){
	/*
		This hook returns an object structured as:
		```
		{
			token, // an axios cancel token
			cancel, // a cancel function
			getNewToken // a function that generates a new cancel token
		}
		```

		`token` and `cancel` can be used to cancel AJAX requests that may be in flight for a while,
		like uploads.

		The same token can be used to cancel multiple requests, e.g. if they were dispatched
		in parallel.

		See the axios documentation for more information on canceling requests:
		https://github.com/axios/axios#cancellation

		e.g., with our AJAX middleware:

		```
			function SomeComponent(){
				const dispatch = useDispatch();
				const { token, cancel } = useCancelToken();
				const [isSubmitted, setIsSubmitted] = useState();

				function makeRequest(){
					dispatch(someNetworkAction(token));
				}

				function cancelRequest(){
					cancel();
				}

				if(!isSubmitted){
					return <button onClick={makeRequest} >Submit</button>;
				} else {
					return <button onClick={cancelRequest} >Cancel</button>;
				}
			}
			...
			function someNetworkAction(token){
				return (dispatch) => {
					return dispatch({
						AJAX: {
							...
							cancelToken: token
						}
					});
				};
			}
		````

		You may also want to use a number of cancel tokens if you want to prevent race conditions from
		messing up repeated requests, e.g. for search as you type.

		In that case, you can use the `getNewToken` function, which will update the `token` and `cancel`
		properties returned by the hook, and return the new token directly.

		If you call the `useCancelToken` hook with a truthy `autoCancel` value, `getNewToken` will automatically
		cancel the last token before generating the new one.

		e.g.
		```
			function SearchBar(){
				const dispatch = useDispatch();
				const { getNewToken } = useCancelToken(true);
				function onChange(e){ // debounce this in real life
					dispatch(search(e.target.value, getNewToken()));
				}
				return <input onChange={onChange} />;
			}

			function search(searchTerm, cancelToken){
				return (dispatch) => {
					return dispatch({
						AJAX: {
							...
							cancelToken,
						}
					});
				};
			}
		```
	*/

	// We use state to keep the references stable across renders.
	// eslint-disable-next-line no-unused-vars
	let [cancelTokenSource, setCancelTokenSource] = useState(axios.CancelToken.source());

	function getNewToken(){
		if(autoCancel){
			cancelTokenSource.cancel();
		}
		const newSource = axios.CancelToken.source();
		setCancelTokenSource(newSource);
		return newSource.token;
	}

	return {
		token: cancelTokenSource.token,
		cancel: cancelTokenSource.cancel,
		getNewToken,
	};
}
